import { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTenant } from '@/components/TenantProvider';
import {
  ACCENT_PRESETS,
  SS_DEFAULT_ACCENT,
  applyAccent,
  isValidHex,
  persistAccent,
  readStoredAccent,
} from '@/lib/accent';
import {
  APP_ICON_VARIANTS,
  applyAppIcon,
  persistAppIcon,
  readStoredAppIcon,
} from '@/lib/appIcon';
import {
  buildExportUrl,
  fetchDailyRecapSettings,
  fetchOwnerProfile,
  fetchQuickReplies,
  saveDailyRecapSettings,
  saveOwnerProfile,
  saveQuickReplies,
  type DailyRecapSettings,
  type OwnerProfile,
  type QuickReplyTemplate,
  fetchNotifPrefs,
  saveNotifPrefs,
  type ServerNotifPrefs,
} from '@/lib/api';
import { resolveInitialPrefs } from '@/lib/notifPrefsSync';

const NOTIF_PREF_KEY = 'ss_notif_pref_v1';
// Bumped to v1.6.1 (2026-07-14) — self-update acceptance test. This deploy
// ships with NO Cloudflare purge on purpose: if an already-installed phone
// picks this up on its own, the no-uninstall/no-purge update path (41be69f
// + Browser Cache TTL = Respect Existing Headers) is proven and the
// pre-launch gate is cleared.
const APP_VERSION = 'v1.6.1';
const SUPPORT_EMAIL = 'support@servicesteady.com';
const HELP_CENTER_URL = 'https://servicesteady.com/help';
const CHANGELOG_URL = 'https://servicesteady.com/whats-new';
const PRIVACY_URL = 'https://servicesteady.com/privacy';
const STOP_TRAILER = 'Reply STOP to opt out.';

interface QuietHours {
  enabled: boolean;
  start: string;
  end: string;
}

interface NotifPrefs {
  followupNeeded: boolean;
  callbackRecap: boolean;
  quoteRecap: boolean;
  quietHours: QuietHours;
  pausedUntil: number | null;
}

const DEFAULT_PREFS: NotifPrefs = {
  followupNeeded: true,
  callbackRecap: true,
  quoteRecap: true,
  quietHours: { enabled: false, start: '21:00', end: '06:00' },
  pausedUntil: null,
};

function readPrefs(): NotifPrefs {
  try {
    const raw = window.localStorage.getItem(NOTIF_PREF_KEY);
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw) as Partial<NotifPrefs>;
    return {
      ...DEFAULT_PREFS,
      ...parsed,
      quietHours: { ...DEFAULT_PREFS.quietHours, ...(parsed.quietHours ?? {}) },
    };
  } catch {
    return DEFAULT_PREFS;
  }
}

function writePrefs(p: NotifPrefs): void {
  try {
    window.localStorage.setItem(NOTIF_PREF_KEY, JSON.stringify(p));
  } catch { /* ignore */ }
}

/**
 * Settings — premium polish v2 (R11 round 2, 2026-05-04).
 *
 * Adds 6 owner-facing sections on top of the existing notif/pause/quiet
 * controls: Profile, Appearance (accent picker), App Icon, Quick Replies,
 * Help & Support, Privacy & Data. The Notification subsystem stays
 * untouched — this file only ADDS sections.
 *
 * Per-section persistence:
 *   - Notif prefs: localStorage + service-worker postMessage (existing)
 *   - Owner profile: Worker /pwa/tenant/:slug/owner-profile (KV)
 *   - Quick replies: Worker /pwa/tenant/:slug/quick-replies (KV)
 *   - Appearance: localStorage (per-device by spec)
 *   - App icon: localStorage (per-device, iOS reinstall caveat surfaced)
 */
export function Settings() {
  const { slug = '' } = useParams<{ slug: string }>();
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState<NotifPrefs>(() => readPrefs());
  // 2026-07-09 (QuietHours_As_TenantSetting) — prefs are now SERVER-persisted
  // (per account, and the Worker defers pushes during quiet hours instead of
  // the SW dropping them). On mount: server wins; if the server has nothing
  // yet, this device's localStorage value migrates up once.
  const serverSynced = useRef(false);
  useEffect(() => {
    if (!slug || serverSynced.current) return;
    serverSynced.current = true;
    void (async () => {
      try {
        const envelope = await fetchNotifPrefs(slug);
        const local = readPrefs();
        const resolved = resolveInitialPrefs(
          envelope,
          local as unknown as ServerNotifPrefs,
          DEFAULT_PREFS as unknown as ServerNotifPrefs,
        );
        setPrefs(resolved.prefs as unknown as NotifPrefs);
        if (resolved.migrate) {
          await saveNotifPrefs(slug, resolved.prefs);
        }
      } catch {
        // offline / auth hiccup — keep the local value; next visit retries
        serverSynced.current = false;
      }
    })();
  }, [slug]);
  // Debounced server write-through on any change after the initial sync.
  const saveTimer = useRef<number | null>(null);
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    if (!slug) return;
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      void saveNotifPrefs(slug, prefs as unknown as ServerNotifPrefs).catch(() => {
        /* next change retries; localStorage below still has it */
      });
    }, 600);
    return () => {
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
    };
  }, [prefs, slug]);

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(t);
  }, []);

  useEffect(() => {
    if (prefs.pausedUntil && prefs.pausedUntil <= now) {
      setPrefs((p) => ({ ...p, pausedUntil: null }));
    }
  }, [now, prefs.pausedUntil]);

  useEffect(() => {
    writePrefs(prefs);
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'ss:notif-prefs',
        prefs,
      });
    }
  }, [prefs]);

  const togglePref = <K extends keyof NotifPrefs>(key: K) => {
    setPrefs((p) => ({ ...p, [key]: !p[key] }));
  };

  const toggleQuietHours = () => {
    setPrefs((p) => ({
      ...p,
      quietHours: { ...p.quietHours, enabled: !p.quietHours.enabled },
    }));
  };

  const setQuietStart = (start: string) => {
    setPrefs((p) => ({ ...p, quietHours: { ...p.quietHours, start } }));
  };
  const setQuietEnd = (end: string) => {
    setPrefs((p) => ({ ...p, quietHours: { ...p.quietHours, end } }));
  };

  const pauseFor = (hours: number | 'morning') => {
    let until: number;
    if (hours === 'morning') {
      const target = new Date();
      target.setHours(7, 0, 0, 0);
      if (target.getTime() <= Date.now()) target.setDate(target.getDate() + 1);
      until = target.getTime();
    } else {
      until = Date.now() + hours * 60 * 60 * 1000;
    }
    setPrefs((p) => ({ ...p, pausedUntil: until }));
  };

  const resumeNow = () => {
    setPrefs((p) => ({ ...p, pausedUntil: null }));
  };

  const handleSignOut = () => {
    const here = window.location.origin;
    window.location.href = `${here}/cdn-cgi/access/logout?returnTo=${encodeURIComponent(here)}`;
  };

  const isPaused = prefs.pausedUntil !== null && prefs.pausedUntil > now;

  // ----- Inline toast for cross-section save confirmations -----
  const [toast, setToast] = useState<string | null>(null);
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 2400);
    return () => window.clearTimeout(t);
  }, [toast]);

  return (
    <div
      className="h-full min-h-0 overflow-y-auto px-4 pb-12"
      style={{
        WebkitOverflowScrolling: 'touch',
        paddingTop: 'env(safe-area-inset-top, 0px)',
      }}
    >
      <div className="px-1 pb-4 pt-2">
        <h1 className="text-2xl font-bold tracking-tight text-ink-900 dark:text-[#E2E8F0]">
          Settings
        </h1>
      </div>

      <SectionCard title="Account">
        <Row label="Business" value={tenant.name} />
        <Row label="Tenant" value={slug} />
      </SectionCard>

      {/* Owner profile (Premium polish #4) */}
      <ProfileSection slug={slug} onSaved={() => setToast('Profile saved')} />

      {/* Quick reply templates (Premium polish #1, editor side) */}
      <QuickRepliesSection slug={slug} onSaved={() => setToast('Quick replies saved')} />

      {/* Appearance — accent color picker (Premium polish #9) */}
      <AppearanceSection onSaved={() => setToast('Accent color updated')} />

      {/* App icon variants (Premium polish #5) */}
      <AppIconSection onSaved={() => setToast('App icon updated')} />

      {/* Pause-for-X */}
      <SectionCard title="Pause notifications">
        {isPaused ? (
          <>
            <div className="px-4 pt-3 pb-2 text-sm text-ink-900 dark:text-[#E2E8F0]">
              Paused until <span className="font-semibold">{formatPauseUntil(prefs.pausedUntil!)}</span>
            </div>
            <button
              type="button"
              onClick={resumeNow}
              className="block w-full border-t border-ink-100 dark:border-[#334155] px-4 py-3 text-center text-[15px] font-semibold text-ss-blue active:bg-ink-50 dark:active:bg-[#1E293B]"
            >
              Resume notifications now
            </button>
          </>
        ) : (
          <div className="grid grid-cols-2 gap-2 p-3">
            <PauseButton label="1 hour" onClick={() => pauseFor(1)} />
            <PauseButton label="2 hours" onClick={() => pauseFor(2)} />
            <PauseButton label="4 hours" onClick={() => pauseFor(4)} />
            <PauseButton label="Until 7am" onClick={() => pauseFor('morning')} />
          </div>
        )}
      </SectionCard>

      <SectionCard title="Quiet hours">
        <ToggleRow
          label="Mute pushes during quiet hours"
          sub="Held until quiet hours end — new leads are still answered, and the notifications arrive when the window opens."
          checked={prefs.quietHours.enabled}
          onToggle={toggleQuietHours}
        />
        <div className={`grid grid-cols-2 gap-2 px-4 py-3 ${prefs.quietHours.enabled ? '' : 'opacity-50'}`}>
          <TimeField
            label="From"
            value={prefs.quietHours.start}
            onChange={setQuietStart}
            disabled={!prefs.quietHours.enabled}
          />
          <TimeField
            label="To"
            value={prefs.quietHours.end}
            onChange={setQuietEnd}
            disabled={!prefs.quietHours.enabled}
          />
        </div>
      </SectionCard>

      <SectionCard title="Notification types">
        <ToggleRow
          label="Follow-up needed alerts"
          sub="Push when Shelby flags a thread as needing your eyes."
          checked={prefs.followupNeeded}
          onToggle={() => togglePref('followupNeeded')}
        />
        <ToggleRow
          label="Callback request recaps"
          sub="Push after a customer asks Shelby to call back."
          checked={prefs.callbackRecap}
          onToggle={() => togglePref('callbackRecap')}
        />
        <ToggleRow
          label="Quote recap pushes"
          sub="Push after Shelby texts a quote so you can see what went out."
          checked={prefs.quoteRecap}
          onToggle={() => togglePref('quoteRecap')}
        />
        <p className="px-4 pb-3 pt-1 text-[11px] leading-relaxed text-ink-400 dark:text-[#64748B]">
          Per-device. Toggle separately on each device you use.
        </p>
      </SectionCard>

      {/* Daily recap push (2026-06-30) — server-side setting, all devices */}
      <DailyRecapSection slug={slug} onSaved={() => setToast('Daily recap settings saved')} />

      {/* Privacy & data (Premium polish #12) */}
      <PrivacySection slug={slug} />

      {/* Help & Support (Premium polish #8) */}
      <SectionCard title="Help & Support">
        <ExternalRow label="Email support" href={`mailto:${SUPPORT_EMAIL}`} hint={SUPPORT_EMAIL} />
        <ExternalRow label="Help center" href={HELP_CENTER_URL} hint="servicesteady.com/help" />
        <ExternalRow label="What's new" href={CHANGELOG_URL} hint="Latest changes" />
        <button
          type="button"
          onClick={() => navigate(`/${slug}/settings/audit-log`)}
          className="block w-full border-t border-ink-100 dark:border-[#334155] px-4 py-3 text-left text-[15px] font-semibold text-ss-blue active:bg-ink-50 dark:active:bg-[#1E293B]"
        >
          View activity log
        </button>
      </SectionCard>

      <SectionCard title="About">
        <Row label="Version" value={APP_VERSION} />
        <Row label="Made by" value="Service Steady" />
        <button
          type="button"
          onClick={() => window.open('https://servicesteady.com', '_blank', 'noopener')}
          className="block w-full px-4 py-3 text-left text-[15px] font-semibold text-ss-blue active:bg-ink-50 dark:active:bg-[#1E293B]"
        >
          servicesteady.com
        </button>
      </SectionCard>

      <SectionCard title="">
        <button
          type="button"
          onClick={handleSignOut}
          className="block w-full px-4 py-3.5 text-center text-[15px] font-semibold text-red-600 dark:text-red-400 active:bg-red-50 dark:active:bg-red-950/30"
        >
          Sign out
        </button>
      </SectionCard>

      {toast && (
        <div
          role="status"
          className="pointer-events-none fixed inset-x-0 bottom-24 z-40 flex justify-center px-6"
        >
          <div className="rounded-full bg-ink-900/95 dark:bg-white/95 px-4 py-2 text-[13px] font-semibold text-white dark:text-ink-900 shadow-ss-md backdrop-blur">
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// Profile section
// ===========================================================================

function ProfileSection({ slug, onSaved }: { slug: string; onSaved: () => void }) {
  const [profile, setProfile] = useState<OwnerProfile>({
    owner_name: '',
    role: '',
    email_signature: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchOwnerProfile(slug)
      .then((p) => {
        if (cancelled) return;
        setProfile(p);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Could not load profile');
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const saved = await saveOwnerProfile(slug, profile);
      setProfile(saved);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard title="Profile">
      {loading ? (
        <div className="px-4 py-4 text-sm text-ink-500 dark:text-[#94A3B8]">Loading…</div>
      ) : (
        <div className="space-y-3 px-4 py-3">
          <LabeledField label="Owner name">
            <input
              type="text"
              value={profile.owner_name}
              onChange={(e) => setProfile({ ...profile, owner_name: e.target.value })}
              maxLength={120}
              className="w-full rounded-lg border border-ink-200 dark:border-[#334155] bg-white dark:bg-[#0F172A] px-3 py-2 text-sm text-ink-900 dark:text-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-ss-blue/30"
            />
          </LabeledField>
          <LabeledField label="Role / title">
            <input
              type="text"
              value={profile.role}
              onChange={(e) => setProfile({ ...profile, role: e.target.value })}
              maxLength={80}
              placeholder="e.g. Owner, GM"
              className="w-full rounded-lg border border-ink-200 dark:border-[#334155] bg-white dark:bg-[#0F172A] px-3 py-2 text-sm text-ink-900 dark:text-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-ss-blue/30"
            />
          </LabeledField>
          <LabeledField label="Email signature">
            <textarea
              value={profile.email_signature}
              onChange={(e) => setProfile({ ...profile, email_signature: e.target.value })}
              maxLength={1000}
              rows={4}
              placeholder={'e.g.\nThanks,\nErnie\nDetail Pros · 203-806-0286'}
              className="w-full resize-y rounded-lg border border-ink-200 dark:border-[#334155] bg-white dark:bg-[#0F172A] px-3 py-2 text-sm text-ink-900 dark:text-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-ss-blue/30"
            />
          </LabeledField>
          {error && (
            <div className="text-xs text-red-600 dark:text-red-400">{error}</div>
          )}
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving}
            className="ss-press w-full rounded-xl px-4 py-2.5 text-sm font-semibold text-[color:var(--ss-accent-fg,white)] disabled:opacity-50"
            style={{ background: 'var(--ss-accent, #2563EB)' }}
          >
            {saving ? 'Saving…' : 'Save profile'}
          </button>
          <p className="text-[11px] text-ink-400 dark:text-[#64748B]">
            Your name and signature show up on outbound messages where supported.
          </p>
        </div>
      )}
    </SectionCard>
  );
}

// ===========================================================================
// Daily recap push (2026-06-30)
// ===========================================================================
//
// Server-persisted (Worker KV) — unlike the per-device notification prefs
// above, this drives a Worker cron, so one setting covers every device.
// Nothing sends until the owner flips the toggle here AND the Worker's
// global DAILY_RECAP_PUSH_ENABLED flag is armed.

function DailyRecapSection({ slug, onSaved }: { slug: string; onSaved: () => void }) {
  const [settings, setSettings] = useState<DailyRecapSettings>({
    enabled: false,
    time: '20:00',
    cadence: 'daily',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchDailyRecapSettings(slug)
      .then((s) => {
        if (cancelled) return;
        setSettings(s);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Could not load recap settings');
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const update = (patch: Partial<DailyRecapSettings>): void => {
    setSettings((s) => ({ ...s, ...patch }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const saved = await saveDailyRecapSettings(slug, settings);
      setSettings(saved);
      setDirty(false);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const cadenceBtn = (value: DailyRecapSettings['cadence'], label: string) => {
    const active = settings.cadence === value;
    return (
      <button
        type="button"
        role="tab"
        aria-selected={active}
        onClick={() => update({ cadence: value })}
        className={
          active
            ? 'rounded-md bg-white px-3 py-1.5 text-[13px] font-semibold text-ink-900 shadow-ss-sm dark:bg-[#334155] dark:text-[#E2E8F0]'
            : 'rounded-md bg-transparent px-3 py-1.5 text-[13px] font-semibold text-ink-500 transition-colors hover:text-ink-700 dark:text-[#94A3B8] dark:hover:text-[#E2E8F0]'
        }
      >
        {label}
      </button>
    );
  };

  return (
    <SectionCard title="Daily recap">
      {loading ? (
        <div className="px-4 py-4 text-sm text-ink-500 dark:text-[#94A3B8]">Loading…</div>
      ) : (
        <>
          <ToggleRow
            label="Evening recap push"
            sub="One push at the end of the day: calls handled, quote value captured, response speed. Skipped automatically on quiet days."
            checked={settings.enabled}
            onToggle={() => update({ enabled: !settings.enabled })}
          />
          <div className={`space-y-3 px-4 py-3 ${settings.enabled ? '' : 'opacity-50'}`}>
            <div className="grid grid-cols-2 items-end gap-2">
              <TimeField
                label="Send at"
                value={settings.time}
                onChange={(next) => update({ time: next })}
                disabled={!settings.enabled}
              />
              <div className="flex flex-col gap-1">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-ink-500 dark:text-[#94A3B8]">
                  Cadence
                </span>
                <div
                  role="tablist"
                  aria-label="Recap cadence"
                  className="inline-flex gap-0.5 self-start rounded-ss-sm bg-ink-100 p-[3px] dark:bg-[#1E293B]"
                >
                  {cadenceBtn('daily', 'Daily')}
                  {cadenceBtn('weekly', 'Weekly')}
                </div>
              </div>
            </div>
            {error && <div className="text-xs text-red-600 dark:text-red-400">{error}</div>}
            {dirty ? (
              <button
                type="button"
                onClick={() => void handleSave()}
                disabled={saving}
                className="ss-press w-full rounded-xl px-4 py-2.5 text-sm font-semibold text-[color:var(--ss-accent-fg,white)] disabled:opacity-50"
                style={{ background: 'var(--ss-accent, #2563EB)' }}
              >
                {saving ? 'Saving…' : 'Save recap settings'}
              </button>
            ) : null}
            <p className="text-[11px] leading-relaxed text-ink-400 dark:text-[#64748B]">
              Sent in your local business timezone. Weekly sends Saturday evening
              with the week&apos;s numbers. Tapping the push opens Reports.
            </p>
          </div>
        </>
      )}
    </SectionCard>
  );
}

// ===========================================================================
// Quick reply templates editor
// ===========================================================================

function ensureStopTrailer(body: string): string {
  const trimmed = body.trim();
  if (!trimmed) return trimmed;
  if (trimmed.endsWith(STOP_TRAILER)) return trimmed;
  return `${trimmed} ${STOP_TRAILER}`;
}

function QuickRepliesSection({
  slug,
  onSaved,
}: {
  slug: string;
  onSaved: () => void;
}) {
  const [templates, setTemplates] = useState<QuickReplyTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchQuickReplies(slug)
      .then((list) => {
        if (cancelled) return;
        setTemplates(list);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Could not load templates');
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const updateTemplate = (id: string, patch: Partial<QuickReplyTemplate>) => {
    setTemplates((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  };

  const removeTemplate = (id: string) => {
    setTemplates((prev) => prev.filter((t) => t.id !== id));
  };

  const moveTemplate = (id: string, dir: -1 | 1) => {
    setTemplates((prev) => {
      const idx = prev.findIndex((t) => t.id === id);
      if (idx < 0) return prev;
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      const item = next[idx]!;
      const swap = next[target]!;
      next[idx] = swap;
      next[target] = item;
      return next;
    });
  };

  const addTemplate = () => {
    const id = `qr_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    setTemplates((prev) => [
      ...prev,
      { id, label: 'New template', body: `Type your message here. ${STOP_TRAILER}` },
    ]);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    // Force-append the STOP trailer on every body before sending; the server
    // also enforces this, but doing it client-side keeps the displayed value
    // consistent with what the server returns post-save.
    const cleaned = templates.map((t) => ({
      ...t,
      label: t.label.trim() || 'Untitled',
      body: ensureStopTrailer(t.body),
    }));
    try {
      const saved = await saveQuickReplies(slug, cleaned);
      setTemplates(saved);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard title="Quick reply templates">
      {loading ? (
        <div className="px-4 py-4 text-sm text-ink-500 dark:text-[#94A3B8]">Loading…</div>
      ) : (
        <div className="space-y-3 px-4 py-3">
          <p className="text-[11px] leading-snug text-ink-500 dark:text-[#94A3B8]">
            Tap the templates icon next to the reply bar to insert one. SMS compliance
            (&ldquo;Reply STOP to opt out.&rdquo;) is appended automatically.
          </p>
          {templates.length === 0 ? (
            <div className="rounded-xl border border-dashed border-ink-200 dark:border-[#334155] px-3 py-4 text-center text-sm text-ink-500 dark:text-[#94A3B8]">
              No templates yet. Add one below.
            </div>
          ) : (
            <ul className="space-y-2">
              {templates.map((t, i) => (
                <li
                  key={t.id}
                  className="rounded-xl border border-ink-100 dark:border-[#334155] bg-ink-50 dark:bg-[#0F172A] p-3"
                >
                  <div className="mb-1 flex items-center gap-2">
                    <input
                      type="text"
                      value={t.label}
                      onChange={(e) => updateTemplate(t.id, { label: e.target.value })}
                      maxLength={40}
                      className="flex-1 rounded-lg border border-ink-200 dark:border-[#334155] bg-white dark:bg-[#1E293B] px-2 py-1.5 text-sm font-semibold text-ink-900 dark:text-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-ss-blue/30"
                    />
                    <ReorderButton dir={-1} disabled={i === 0} onClick={() => moveTemplate(t.id, -1)} />
                    <ReorderButton dir={1} disabled={i === templates.length - 1} onClick={() => moveTemplate(t.id, 1)} />
                    <button
                      type="button"
                      onClick={() => removeTemplate(t.id)}
                      aria-label="Delete template"
                      className="ss-press flex h-7 w-7 items-center justify-center rounded-full bg-white dark:bg-[#1E293B] text-red-600 dark:text-red-400"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                        <path d="M10 11v6M14 11v6" />
                      </svg>
                    </button>
                  </div>
                  <textarea
                    value={t.body}
                    onChange={(e) => updateTemplate(t.id, { body: e.target.value })}
                    onBlur={(e) => updateTemplate(t.id, { body: ensureStopTrailer(e.target.value) })}
                    maxLength={320}
                    rows={2}
                    className="w-full resize-y rounded-lg border border-ink-200 dark:border-[#334155] bg-white dark:bg-[#1E293B] px-2 py-1.5 text-xs text-ink-700 dark:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-ss-blue/30"
                  />
                </li>
              ))}
            </ul>
          )}
          {error && (
            <div className="text-xs text-red-600 dark:text-red-400">{error}</div>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={addTemplate}
              className="ss-press flex-1 rounded-xl border border-ink-200 dark:border-[#334155] px-3 py-2 text-sm font-semibold text-ink-900 dark:text-[#E2E8F0]"
            >
              Add template
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving}
              className="ss-press flex-1 rounded-xl px-3 py-2 text-sm font-semibold text-[color:var(--ss-accent-fg,white)] disabled:opacity-50"
              style={{ background: 'var(--ss-accent, #2563EB)' }}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </SectionCard>
  );
}

function ReorderButton({
  dir,
  disabled,
  onClick,
}: {
  dir: -1 | 1;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={dir === -1 ? 'Move up' : 'Move down'}
      className="ss-press flex h-7 w-7 items-center justify-center rounded-full bg-white dark:bg-[#1E293B] text-ink-700 dark:text-[#94A3B8] disabled:opacity-30"
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5">
        {dir === -1 ? <polyline points="18 15 12 9 6 15" /> : <polyline points="6 9 12 15 18 9" />}
      </svg>
    </button>
  );
}

// ===========================================================================
// Appearance — accent color picker
// ===========================================================================

function AppearanceSection({ onSaved }: { onSaved: () => void }) {
  const [accent, setAccent] = useState<string>(() => readStoredAccent());
  const [customHex, setCustomHex] = useState<string>('');

  const matchedPreset = useMemo(
    () => ACCENT_PRESETS.find((p) => p.hex.toLowerCase() === accent.toLowerCase()),
    [accent],
  );

  const commit = (hex: string) => {
    if (!isValidHex(hex)) return;
    setAccent(hex);
    persistAccent(hex);
    applyAccent(hex);
    onSaved();
  };

  return (
    <SectionCard title="Appearance">
      <div className="px-4 py-3">
        <div className="mb-2 text-xs text-ink-500 dark:text-[#94A3B8]">
          Pick an accent color for buttons and active highlights.
        </div>
        <div className="grid grid-cols-6 gap-2">
          {ACCENT_PRESETS.map((p) => {
            const active = p.hex.toLowerCase() === accent.toLowerCase();
            return (
              <button
                key={p.hex}
                type="button"
                onClick={() => commit(p.hex)}
                aria-label={`Use ${p.label} accent`}
                className={`group relative flex h-12 w-12 items-center justify-center rounded-full transition-transform active:scale-95 ${active ? 'ring-2 ring-offset-2 ring-offset-white dark:ring-offset-[#1E293B]' : ''}`}
                style={{ background: p.hex, color: '#FFFFFF', ...(active ? { boxShadow: `0 0 0 2px ${p.hex}` } : {}) }}
              >
                {active ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : null}
              </button>
            );
          })}
        </div>
        <div className="mt-3 flex items-center gap-2">
          <span className="text-xs text-ink-500 dark:text-[#94A3B8]">Custom hex</span>
          <input
            type="text"
            value={customHex}
            onChange={(e) => setCustomHex(e.target.value)}
            placeholder="#RRGGBB"
            maxLength={7}
            className="flex-1 rounded-lg border border-ink-200 dark:border-[#334155] bg-white dark:bg-[#0F172A] px-2 py-1.5 text-xs uppercase text-ink-900 dark:text-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-ss-blue/30"
          />
          <button
            type="button"
            onClick={() => commit(customHex)}
            disabled={!isValidHex(customHex)}
            className="ss-press rounded-lg px-3 py-1.5 text-xs font-semibold text-[color:var(--ss-accent-fg,white)] disabled:opacity-40"
            style={{ background: 'var(--ss-accent, #2563EB)' }}
          >
            Apply
          </button>
        </div>
        <div className="mt-2 flex items-center justify-between text-[11px] text-ink-400 dark:text-[#64748B]">
          <span>
            Active: <span style={{ color: accent }} className="font-semibold">{matchedPreset?.label ?? accent}</span>
          </span>
          {accent.toLowerCase() !== SS_DEFAULT_ACCENT.toLowerCase() && (
            <button
              type="button"
              onClick={() => commit(SS_DEFAULT_ACCENT)}
              className="font-semibold text-ss-blue"
            >
              Reset to SS Blue
            </button>
          )}
        </div>
      </div>
    </SectionCard>
  );
}

// ===========================================================================
// App icon variants
// ===========================================================================

function AppIconSection({ onSaved }: { onSaved: () => void }) {
  const [iconId, setIconId] = useState<string>(() => readStoredAppIcon());

  const choose = (id: string) => {
    setIconId(id);
    persistAppIcon(id);
    applyAppIcon(id);
    onSaved();
  };

  return (
    <SectionCard title="App icon">
      <div className="px-4 py-3">
        <div className="grid grid-cols-4 gap-3">
          {APP_ICON_VARIANTS.map((v) => {
            const active = v.id === iconId;
            return (
              <button
                key={v.id}
                type="button"
                onClick={() => choose(v.id)}
                className={`flex flex-col items-center gap-1 rounded-xl p-2 transition-transform active:scale-95 ${active ? 'bg-ink-100 dark:bg-[#0F172A]' : ''}`}
                aria-label={`Use ${v.label} app icon`}
              >
                <div className="relative h-14 w-14 overflow-hidden rounded-2xl shadow-ss-sm">
                  <img
                    src={v.appleTouchIcon}
                    alt=""
                    className="h-full w-full object-cover"
                    onError={(e) => {
                      // Solid swatch fallback for variants whose PNGs haven't
                      // been dropped into /public/icons/variants/ yet.
                      const img = e.currentTarget as HTMLImageElement;
                      img.style.display = 'none';
                      const parent = img.parentElement;
                      if (parent && !parent.dataset.fallback) {
                        parent.dataset.fallback = '1';
                        parent.style.background = v.swatch;
                        const init = document.createElement('span');
                        init.textContent = 'SS';
                        init.className = 'absolute inset-0 flex items-center justify-center text-base font-bold';
                        init.style.color = v.swatch.toLowerCase() === '#ffffff' ? '#0F172A' : '#FFFFFF';
                        parent.appendChild(init);
                      }
                    }}
                  />
                  {active && (
                    <div className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500 text-white">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3.5} strokeLinecap="round" strokeLinejoin="round" className="h-3 w-3">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="text-[11px] font-semibold text-ink-900 dark:text-[#E2E8F0]">
                  {v.label}
                </div>
                {v.hint && (
                  <div className="text-[10px] leading-tight text-ink-400 dark:text-[#64748B]">
                    {v.hint}
                  </div>
                )}
              </button>
            );
          })}
        </div>
        <p className="mt-3 text-[11px] leading-snug text-ink-500 dark:text-[#94A3B8]">
          Heads up: iOS caches the home-screen icon at install time. To refresh it,
          remove the app from your home screen and re-add it from Safari.
        </p>
      </div>
    </SectionCard>
  );
}

// ===========================================================================
// Privacy & data
// ===========================================================================

function PrivacySection({ slug }: { slug: string }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [collectsOpen, setCollectsOpen] = useState(false);

  return (
    <SectionCard title="Privacy & data">
      <button
        type="button"
        onClick={() => setCollectsOpen((s) => !s)}
        className="flex w-full items-center justify-between border-b border-ink-100 dark:border-[#334155] px-4 py-3 text-left"
      >
        <span className="text-sm font-semibold text-ink-900 dark:text-[#E2E8F0]">
          What we collect
        </span>
        <span className="text-xs text-ink-500 dark:text-[#94A3B8]">
          {collectsOpen ? 'Hide' : 'Show'}
        </span>
      </button>
      {collectsOpen && (
        <div className="border-b border-ink-100 dark:border-[#334155] bg-ink-50 dark:bg-[#0F172A] px-4 py-3 text-xs leading-relaxed text-ink-700 dark:text-[#94A3B8]">
          We store your owner profile, quick reply templates, push notification
          subscriptions, and an action audit log. Customer phone numbers and
          conversation contents live in your GoHighLevel sub-account &mdash; we
          access them only as needed to render the conversation views you see in this app.
          We never sell your data.
        </div>
      )}
      <a
        href={buildExportUrl(`/pwa/data/export?slug=${encodeURIComponent(slug)}`)}
        download
        className="block w-full border-b border-ink-100 dark:border-[#334155] px-4 py-3 text-left text-[15px] font-semibold text-ss-blue active:bg-ink-50 dark:active:bg-[#1E293B]"
      >
        Export my data
      </a>
      <a
        href={PRIVACY_URL}
        target="_blank"
        rel="noreferrer"
        className="block w-full border-b border-ink-100 dark:border-[#334155] px-4 py-3 text-left text-[15px] font-semibold text-ss-blue active:bg-ink-50 dark:active:bg-[#1E293B]"
      >
        View privacy policy
      </a>
      <button
        type="button"
        onClick={() => setConfirmDelete(true)}
        className="block w-full px-4 py-3 text-left text-[15px] font-semibold text-red-600 dark:text-red-400 active:bg-red-50 dark:active:bg-red-950/30"
      >
        Delete my account
      </button>
      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
          role="dialog"
          aria-modal="true"
          onClick={() => setConfirmDelete(false)}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-white dark:bg-[#0F172A] p-5 text-center shadow-ss-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-2 text-base font-semibold text-ink-900 dark:text-[#E2E8F0]">
              Delete my account
            </div>
            <p className="mb-4 text-sm text-ink-500 dark:text-[#94A3B8]">
              Account deletion is handled by our team. Email{' '}
              <a className="text-ss-blue" href={`mailto:${SUPPORT_EMAIL}?subject=Delete%20my%20account`}>
                {SUPPORT_EMAIL}
              </a>{' '}
              and we&rsquo;ll process the request within one business day.
            </p>
            <button
              type="button"
              onClick={() => setConfirmDelete(false)}
              className="ss-press w-full rounded-xl bg-ink-100 dark:bg-[#1E293B] px-4 py-2.5 text-sm font-semibold text-ink-900 dark:text-[#E2E8F0]"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </SectionCard>
  );
}

// ===========================================================================
// Layout primitives
// ===========================================================================

function formatPauseUntil(ms: number): string {
  const d = new Date(ms);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return sameDay ? time : `${d.toLocaleDateString([], { weekday: 'short' })} ${time}`;
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-5">
      {title ? (
        <div className="px-4 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-ink-500 dark:text-[#94A3B8]">
          {title}
        </div>
      ) : null}
      <div className="overflow-hidden rounded-2xl border border-ink-100 dark:border-[#334155] bg-white dark:bg-[#1E293B]">
        {children}
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between border-b border-ink-100 dark:border-[#334155] px-4 py-3 last:border-b-0">
      <span className="text-sm text-ink-500 dark:text-[#94A3B8]">{label}</span>
      <span className="text-sm font-medium text-ink-900 dark:text-[#E2E8F0]">{value}</span>
    </div>
  );
}

function ExternalRow({
  label,
  href,
  hint,
}: {
  label: string;
  href: string;
  hint?: string;
}) {
  return (
    <a
      href={href}
      target={href.startsWith('mailto:') ? undefined : '_blank'}
      rel={href.startsWith('mailto:') ? undefined : 'noreferrer'}
      className="flex items-center justify-between border-b border-ink-100 dark:border-[#334155] px-4 py-3 last:border-b-0 active:bg-ink-50 dark:active:bg-[#0F172A]"
    >
      <div className="min-w-0">
        <div className="text-sm font-semibold text-ink-900 dark:text-[#E2E8F0]">{label}</div>
        {hint && (
          <div className="mt-0.5 truncate text-xs text-ink-500 dark:text-[#94A3B8]">{hint}</div>
        )}
      </div>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="ml-3 h-4 w-4 text-ink-400 dark:text-[#64748B]">
        <polyline points="9 18 15 12 9 6" />
      </svg>
    </a>
  );
}

function LabeledField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-ink-500 dark:text-[#94A3B8]">
        {label}
      </span>
      {children}
    </label>
  );
}

function ToggleRow({
  label,
  sub,
  checked,
  onToggle,
}: {
  label: string;
  sub?: string;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="flex items-center gap-3 border-b border-ink-100 dark:border-[#334155] px-4 py-3 last:border-b-0">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-ink-900 dark:text-[#E2E8F0]">{label}</div>
        {sub ? <div className="mt-0.5 text-xs text-ink-500 dark:text-[#94A3B8]">{sub}</div> : null}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={onToggle}
        className={[
          'relative h-7 w-12 flex-shrink-0 rounded-full transition-colors duration-200',
          checked ? 'bg-ss-blue' : 'bg-ink-200 dark:bg-[#334155]',
        ].join(' ')}
      >
        <span
          className={[
            'absolute top-0.5 h-6 w-6 rounded-full bg-white shadow-ss-sm transition-all duration-200 ease-ss-spring',
            checked ? 'left-[22px]' : 'left-0.5',
          ].join(' ')}
        />
      </button>
    </div>
  );
}

function PauseButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-xl border border-ink-200 dark:border-[#334155] bg-ink-50 dark:bg-[#0F172A] px-3 py-3 text-sm font-semibold text-ink-900 dark:text-[#E2E8F0] active:bg-ink-100 dark:active:bg-[#1E293B] transition-colors"
    >
      {label}
    </button>
  );
}

function TimeField({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  disabled: boolean;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-ink-500 dark:text-[#94A3B8]">
        {label}
      </span>
      <input
        type="time"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-lg border border-ink-200 dark:border-[#334155] bg-white dark:bg-[#0F172A] px-3 py-2.5 text-sm text-ink-900 dark:text-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-ss-blue/40 disabled:opacity-60"
      />
    </label>
  );
}
